import { useQuery, useMutation } from '@vue/apollo-composable';
import { useToast } from 'primevue/usetoast';
import { useSchoolQuery } from '@/composables/queries/useSchoolQuery';
import type { Grade, GradeGroup, SchoolGradeInput } from '@/types/composition-functions';
import { GET_SCHOOL_GRADE_GROUPS } from '@/graphql/Queries/gradeSystem';
import {
  CREATE_SCHOOL_GRADE,
  UPDATE_SCHOOL_GRADE,
  DELETE_SCHOOL_GRADE,
  UPDATE_SCHOOL_GRADE_GROUP,
  DELETE_SCHOOL_GRADE_GROUP,
  CREATE_SCHOOL_GRADE_BULK,
} from '@/graphql/mutations/gradeSystem';

export function useGradeSystem() {
  const toast = useToast();
  const { school } = useSchoolQuery();

  const schoolId = computed(() => school.value?.id);

  // Fetch Grade Groups
  const { result, loading, refetch, error } = useQuery(
    GET_SCHOOL_GRADE_GROUPS,
    null,
    {
      fetchPolicy: 'network-only',
    },
  );

  const gradeGroups = computed<GradeGroup[]>(() => result.value?.getSchoolGradeGroups || []);

  // Create Grade Bulk
  const { mutate: createGradeBulkMutate, loading: createBulkLoading } = useMutation(CREATE_SCHOOL_GRADE_BULK);

  const createGradeBulk = async(inputs: SchoolGradeInput[]) => {
    const res = await createGradeBulkMutate({
      inputs: inputs,
    });
    if (res?.data?.createSchoolGradeBulk) {
      toast.add({ severity: 'success', summary: 'Success', detail: 'Grades created successfully', life: 3000 });
      refetch();
      return true;
    }
    return false;
  };

  // Create Grade
  const { mutate: createGradeMutate, loading: createLoading } = useMutation(CREATE_SCHOOL_GRADE);

  const createGrade = async(input: SchoolGradeInput, showToast: boolean = true) => {
    const res = await createGradeMutate({
      createGradeInput: { ...input },
    });
    if (res?.data?.createSchoolGrade) {
      if (showToast) {
        toast.add({ severity: 'success', summary: 'Success', detail: 'Grade created successfully', life: 3000 });
      }
      refetch();
      return true;
    }
    return false;
  };

  // Update Grade
  const { mutate: updateGradeMutate, loading: updateLoading } = useMutation(UPDATE_SCHOOL_GRADE);

  const updateGrade = async(id: string, input: SchoolGradeInput) => {
    const res = await updateGradeMutate({
      id,
      updateGradeInput: { ...input },
    });
    if (res?.data?.updateSchoolGrade) {
      toast.add({ severity: 'success', summary: 'Success', detail: 'Grade updated successfully', life: 3000 });
      refetch();
      return true;
    }
    return false;
  };

  // Delete Grade
  const { mutate: deleteGradeMutate, loading: deleteLoading } = useMutation(DELETE_SCHOOL_GRADE);

  const deleteGrade = async(id: string) => {
    try {
      const res = await deleteGradeMutate({ id });
      if (res?.data?.deleteSchoolGrade) {
        toast.add({ severity: 'success', summary: 'Success', detail: 'Grade deleted successfully', life: 3000 });
        refetch();
        return true;
      }
    } catch (e) {
      toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to delete grade', life: 3000 });
    }
    return false;
  };

  // Update Grade Group
  const { mutate: updateGroupMutate, loading: updateGroupLoading } = useMutation(UPDATE_SCHOOL_GRADE_GROUP);

  const updateGradeGroup = async(id: string, name: string) => {
    try {
      const res = await updateGroupMutate({ id, name });
      if (res?.data?.updateSchoolGradeGroup) {
        toast.add({ severity: 'success', summary: 'Success', detail: 'Grade group updated successfully', life: 3000 });
        refetch();
        return true;
      }
    } catch (e) {
      toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to update grade group', life: 3000 });
    }
    return false;
  };

  // Delete Grade Group
  const { mutate: deleteGroupMutate, loading: deleteGroupLoading } = useMutation(DELETE_SCHOOL_GRADE_GROUP);

  const deleteGradeGroup = async(id: string) => {
    try {
      const res = await deleteGroupMutate({ id });
      if (res?.data?.deleteSchoolGradeGroup) {
        toast.add({ severity: 'success', summary: 'Success', detail: 'Grade group deleted successfully', life: 3000 });
        refetch();
        return true;
      }
    } catch (e) {
      toast.add({ severity: 'error', summary: 'Error', detail: e instanceof Error ? e.message : 'Failed to delete grade group', life: 3000 });
    }
    return false;
  };

  return {
    gradeGroups,
    loading,
    error,
    createGrade,
    createLoading,
    createGradeBulk,
    createBulkLoading,
    updateGrade,
    updateLoading,
    deleteGrade,
    deleteLoading,
    updateGradeGroup,
    updateGroupLoading,
    deleteGradeGroup,
    deleteGroupLoading,
    refetch,
  };
}

